import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import {
  Bike,
  Mail,
  MapPin,
  MessageCircle,
  Shield,
  Wrench,
  Gauge,
  Gift,
  Ban,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Phone,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { zehmotocaUnidades, zehmotocaHeroSlides } from '@/data/zehmotocaUnidades';
import { clienteMobileService } from '@/services/clienteMobileService';

/** WhatsApp comercial / matriz (site institucional). */
const COMERCIAL_WA = 'https://wa.me/5583993491717';
const COMERCIAL_WA_DISPLAY = '(83) 99349-1717';
const EMAIL = 'comercial@zehmotoca.com.br';
const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/souzehmotoca/';

const nav = [
  { href: '#inicio', label: 'Início' },
  { href: '#sobre', label: 'Sobre' },
  { href: '#diferenciais', label: 'Diferenciais' },
  { href: '#publicacoes', label: 'Publicações' },
  { href: '#planos', label: 'Planos' },
  { href: '#faq', label: 'Dúvidas' },
  { href: '#contato', label: 'Contato' },
];

const diferenciais = [
  {
    icon: Wrench,
    title: 'Oficina própria',
    text: 'Oficina própria para manutenção rápida e com qualidade. Você roda tranquilo.',
  },
  {
    icon: Ban,
    title: 'Sem consulta SPC/Serasa',
    text: 'Planos pensados para quem quer trabalhar — sem burocracia desnecessária.',
  },
  {
    icon: Shield,
    title: 'Seguro e IPVA inclusos',
    text: 'Mais previsibilidade: seguro e IPVA no pacote, conforme o plano contratado.',
  },
  {
    icon: RefreshCw,
    title: 'Manutenção e revisão',
    text: 'Manutenções e revisões garantidas durante o período do contrato.',
  },
  {
    icon: Gauge,
    title: 'Sem limite de quilometragem',
    text: 'Rode o quanto precisar para trabalhar e viver a cidade.',
  },
  {
    icon: Gift,
    title: 'Projeto de propriedade',
    text: 'Cumprindo o prazo do plano, a moto pode ser sua. Consulte condições no contrato.',
  },
];

const faqs = [
  {
    q: 'Como faço para alugar uma moto?',
    a: 'Escolha o modelo, fale com a equipe pelo WhatsApp e tenha em mãos CNH e comprovante de residência. O time orienta o restante do processo.',
  },
  {
    q: 'Tenho restrições no SPC/Serasa. Posso alugar?',
    a: 'Sim. A Zeh Motoca trabalha com planos acessíveis, inclusive para quem tem restrições — entre em contato e saiba as condições.',
  },
  {
    q: 'Como funciona o plano de locação?',
    a: 'Você aluga por um período definido no contrato, com parcelas combinadas. No fim do prazo, conforme regras do plano, a moto pode ser sua.',
  },
  {
    q: 'E se acontecer acidente, roubo ou apreensão?',
    a: 'Em emergências, acione a central e o seguro conforme o contrato. Podem existir taxas de coparticipação — veja os detalhes no documento assinado.',
  },
];

function SectionTitle({ eyebrow, title }: { eyebrow?: string; title: string }) {
  return (
    <div className="text-center max-w-2xl mx-auto mb-12">
      {eyebrow ? (
        <p className="text-sm font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-2">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="text-3xl sm:text-4xl font-bold text-foreground font-[family-name:var(--font-heading)]">
        {title}
      </h2>
    </div>
  );
}

function CanalRow({ label, href, display }: { label: string; href: string; display: string }) {
  const isWa = href.includes('wa.me');
  const isTel = href.startsWith('tel:');
  return (
    <a
      href={href}
      target={isTel ? '_self' : '_blank'}
      rel={isTel ? undefined : 'noopener noreferrer'}
      className="flex items-center justify-between gap-2 rounded-lg border border-border/80 bg-muted/30 px-3 py-2 text-sm hover:border-amber-400/50 transition-colors"
    >
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="flex items-center gap-1.5 font-medium text-foreground text-right min-w-0">
        {isWa ? <MessageCircle className="h-3.5 w-3.5 text-amber-600 shrink-0" /> : <Phone className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
        <span className="truncate">{display}</span>
      </span>
    </a>
  );
}

type ContentFilter = 'all' | 'reel' | 'post';

const feedFilterConfig: Array<{ id: ContentFilter; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'reel', label: 'Reels' },
  { id: 'post', label: 'Publicações' },
];

export function ZehMotocaLandingPage() {
  const [heroIdx, setHeroIdx] = useState(0);
  const [contentFilter, setContentFilter] = useState<ContentFilter>('all');
  const [feedIdx, setFeedIdx] = useState(0);
  const heroLen = zehmotocaHeroSlides.length;
  const slide = zehmotocaHeroSlides[heroIdx];

  const heroPrev = useCallback(() => {
    setHeroIdx((i) => (i - 1 + heroLen) % heroLen);
  }, [heroLen]);

  const heroNext = useCallback(() => {
    setHeroIdx((i) => (i + 1) % heroLen);
  }, [heroLen]);

  const instagramQuery = useQuery({
    queryKey: ['zehmotoca-instagram-feed'],
    queryFn: () => clienteMobileService.getZehMotocaInstagramFeed(),
    staleTime: 5 * 60 * 1000,
  });

  const filteredFeed = useMemo(() => {
    const list = instagramQuery.data ?? [];
    if (contentFilter === 'all') return list;
    return list.filter((item) => item.type === contentFilter);
  }, [instagramQuery.data, contentFilter]);

  const feedLen = filteredFeed.length;

  useEffect(() => {
    setFeedIdx(0);
  }, [contentFilter]);

  useEffect(() => {
    if (feedLen <= 1) return;
    const timer = window.setInterval(() => {
      setFeedIdx((i) => (i + 1) % feedLen);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [feedLen]);

  const safeFeedIdx = feedLen > 0 ? feedIdx % feedLen : 0;
  const activeFeedItem = feedLen > 0 ? filteredFeed[safeFeedIdx] : null;

  const feedPrev = useCallback(() => {
    if (feedLen <= 1) return;
    setFeedIdx((i) => (i - 1 + feedLen) % feedLen);
  }, [feedLen]);

  const feedNext = useCallback(() => {
    if (feedLen <= 1) return;
    setFeedIdx((i) => (i + 1) % feedLen);
  }, [feedLen]);

  return (
    <>
      <Helmet>
        <title>Zeh Motoca — Locação de motos | João Pessoa, Natal e Fortaleza</title>
        <meta
          name="description"
          content="Zeh Motoca: locação de motos com planos acessíveis, oficina própria e unidades em João Pessoa, Natal e Fortaleza. Fale pelo WhatsApp."
        />
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 border-b border-border/80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
            <a href="#inicio" className="flex items-center gap-2 shrink-0">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-400 text-zinc-900">
                <Bike className="h-5 w-5" aria-hidden />
              </span>
              <span className="font-bold text-lg tracking-tight font-[family-name:var(--font-heading)]">
                Zeh Motoca
              </span>
            </a>
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
              {nav.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="hover:text-foreground transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="flex items-center gap-2 shrink-0">
              <a href={COMERCIAL_WA} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="hidden sm:inline-flex gap-1.5">
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </Button>
              </a>
              <Link to="/cliente/login">
                <Button size="sm" className="bg-amber-400 text-zinc-900 hover:bg-amber-500 border-0">
                  Área do cliente
                </Button>
              </Link>
            </div>
          </div>
        </header>

        <section
          id="inicio"
          className="relative scroll-mt-20 overflow-hidden border-b border-border bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white"
        >
          <div className="pointer-events-none absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_20%,#fbbf24,transparent_50%)]" />
          <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
            <div className="flex items-start justify-between gap-4">
              <p className="text-amber-400 font-semibold text-sm uppercase tracking-widest mb-0">
                Locação de motos
              </p>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={heroPrev}
                  className="rounded-full border border-zinc-600 p-2 text-white hover:bg-white/10 transition-colors"
                  aria-label="Slide anterior"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={heroNext}
                  className="rounded-full border border-zinc-600 p-2 text-white hover:bg-white/10 transition-colors"
                  aria-label="Próximo slide"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mt-4 min-h-[200px] sm:min-h-[220px]">
              <h1 className="max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight font-[family-name:var(--font-heading)]">
                {(() => {
                  const [antes, depois = ''] = slide.titulo.split('Zeh Motoca');
                  return (
                    <>
                      {antes}
                      <span className="text-amber-400">Zeh Motoca</span>
                      {depois}
                    </>
                  );
                })()}
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-zinc-300 leading-relaxed">{slide.subtitulo}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Cidades">
              {zehmotocaHeroSlides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={i === heroIdx}
                  onClick={() => setHeroIdx(i)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    i === heroIdx
                      ? 'bg-amber-400 text-zinc-900'
                      : 'bg-white/10 text-zinc-300 hover:bg-white/15'
                  }`}
                >
                  {s.cidade}
                </button>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a href={slide.whatsappHref} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gap-2 bg-amber-400 text-zinc-900 hover:bg-amber-500 border-0">
                  <MessageCircle className="h-5 w-5" />
                  Falar no WhatsApp ({slide.cidade})
                </Button>
              </a>
              <Link to="/cliente/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/90 bg-white/5 text-white hover:bg-white/15 hover:text-white"
                >
                  Entrar na plataforma
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section id="sobre" className="scroll-mt-20 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <SectionTitle eyebrow="Sobre nós" title="Zeh Motoca" />
            <div className="grid gap-10 lg:grid-cols-2 lg:gap-14 items-start">
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Por aqui não se trata só de motos — se trata de <strong className="text-foreground">movimento</strong>
                  , de liberdade e de caminhar (ou melhor, acelerar) junto com quem faz a cidade girar.
                </p>
                <p>
                  Queremos oferecer motos com planos acessíveis, cuidando da moto e de quem depende dela para trabalhar
                  e se organizar — em João Pessoa, Natal, Fortaleza e região.
                </p>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <h3 className="font-semibold text-foreground mb-2">Nossa missão</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Transformar mobilidade em oportunidade: motos de qualidade e planos que fazem sentido no bolso.
                  </p>
                </div>
                <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <h3 className="font-semibold text-foreground mb-2">Nossa visão</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Ser referência ao impulsionar a vida de quem vive na correria — liberdade, renda e novas
                    possibilidades.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="diferenciais"
          className="scroll-mt-20 border-y border-border bg-muted/40 py-16 sm:py-20 px-4 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-6xl">
            <SectionTitle eyebrow="Por que a Zeh" title="Diferenciais" />
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {diferenciais.map((d) => (
                <div
                  key={d.title}
                  className="rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-400/15 text-amber-600 dark:text-amber-400">
                    <d.icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{d.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="publicacoes"
          className="scroll-mt-20 border-y border-border bg-gradient-to-b from-background to-muted/20 py-16 sm:py-20 px-4 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-6xl">
            <SectionTitle eyebrow="@souzehmotoca" title="Reels e publicações" />

            <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
              {feedFilterConfig.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setContentFilter(filter.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    contentFilter === filter.id
                      ? 'bg-amber-400 text-zinc-900'
                      : 'bg-card text-muted-foreground border border-border hover:text-foreground'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {instagramQuery.isLoading ? (
              <div className="grid gap-6 md:grid-cols-2">
                <div className="h-80 rounded-2xl border border-border bg-card animate-pulse" />
                <div className="h-80 rounded-2xl border border-border bg-card animate-pulse" />
              </div>
            ) : null}

            {!instagramQuery.isLoading && activeFeedItem ? (
              <div className="grid gap-8 lg:grid-cols-[1.45fr_1fr] items-stretch">
                <a
                  href={activeFeedItem.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative min-h-[380px] overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
                >
                  {activeFeedItem.thumbnail ? (
                    <img
                      src={activeFeedItem.thumbnail}
                      alt={activeFeedItem.caption ?? 'Publicação do Instagram da Zeh Motoca'}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="h-full w-full bg-muted" />
                  )}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-6">
                    <div className="mb-2 inline-flex rounded-full bg-amber-400 px-3 py-1 text-xs font-bold uppercase tracking-wide text-zinc-900">
                      {activeFeedItem.type === 'reel' ? 'Reel' : 'Publicação'}
                    </div>
                    <p className="line-clamp-3 text-sm sm:text-base text-zinc-100 leading-relaxed">
                      {activeFeedItem.caption ?? 'Confira este conteúdo no Instagram da Zeh Motoca.'}
                    </p>
                  </div>
                </a>

                <div className="rounded-3xl border border-border bg-card p-6 shadow-sm flex flex-col">
                  <p className="text-sm font-semibold text-foreground">Últimas do perfil</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Acompanhe novidades, bastidores e conteúdos da Zeh Motoca diretamente no Instagram.
                  </p>

                  <div className="mt-6 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={feedPrev}
                      disabled={feedLen <= 1}
                      className="rounded-full border border-border p-2 text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Publicação anterior"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={feedNext}
                      disabled={feedLen <= 1}
                      className="rounded-full border border-border p-2 text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Próxima publicação"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {filteredFeed.map((item, index) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setFeedIdx(index)}
                        className={`h-2.5 rounded-full transition-all ${
                          safeFeedIdx === index ? 'w-8 bg-amber-400' : 'w-2.5 bg-muted-foreground/40 hover:bg-muted-foreground/60'
                        }`}
                        aria-label={`Ir para item ${index + 1}`}
                      />
                    ))}
                  </div>

                  <div className="mt-auto pt-8">
                    <a href={INSTAGRAM_PROFILE_URL} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-amber-400 text-zinc-900 hover:bg-amber-500 border-0">
                        Ver perfil no Instagram
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ) : null}

            {!instagramQuery.isLoading && !activeFeedItem ? (
              <div className="rounded-2xl border border-border bg-card p-8 sm:p-10 text-center shadow-sm">
                <p className="text-lg font-semibold text-foreground">
                  Nenhum conteúdo disponível no momento.
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {instagramQuery.isError
                    ? 'Não foi possível carregar o feed agora. Você pode acessar o perfil diretamente.'
                    : 'Novas publicações aparecerão aqui automaticamente.'}
                </p>
                <a
                  href={INSTAGRAM_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex mt-6"
                >
                  <Button className="bg-amber-400 text-zinc-900 hover:bg-amber-500 border-0">
                    Abrir @souzehmotoca
                  </Button>
                </a>
              </div>
            ) : null}
          </div>
        </section>

        <section id="planos" className="scroll-mt-20 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <SectionTitle eyebrow="Contratação" title="Planos de locação" />
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Modelos e condições variam conforme disponibilidade e unidade. Fale com a equipe pelo WhatsApp para
              simular valores, entrada e prazo — sem compromisso.
            </p>
            <a href={COMERCIAL_WA} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="gap-2 bg-amber-400 text-zinc-900 hover:bg-amber-500 border-0">
                <MessageCircle className="h-5 w-5" />
                Quero saber mais
              </Button>
            </a>
          </div>
        </section>

        <section id="faq" className="scroll-mt-20 border-t border-border bg-muted/30 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <SectionTitle eyebrow="Ajuda rápida" title="Dúvidas frequentes" />
            <div className="space-y-3">
              {faqs.map((item) => (
                <details
                  key={item.q}
                  className="group rounded-xl border border-border bg-card px-4 py-1 shadow-sm open:shadow-md"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-medium text-foreground [&::-webkit-details-marker]:hidden">
                    {item.q}
                    <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="contato" className="scroll-mt-20 py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <SectionTitle eyebrow="Fale conosco" title="Unidades e telefones" />
            <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto -mt-8 mb-10">
              Canais por unidade (emergência, oficina, atendimento e financeiro). O 0800 de emergência da unidade Natal
              abre a discagem no aparelho; demais linhas com WhatsApp abrem o app.
            </p>

            <div className="grid gap-8 lg:grid-cols-3">
              {zehmotocaUnidades.map((u) => (
                <div
                  key={u.id}
                  className="rounded-2xl border border-border bg-card p-6 shadow-sm flex flex-col gap-4"
                >
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{u.nome}</h3>
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium mt-0.5">{u.cidadeUf}</p>
                  </div>
                  <div className="flex gap-3 text-sm text-muted-foreground">
                    <MapPin className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="leading-relaxed">
                      {u.enderecoLinhas.map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 mt-auto">
                    <CanalRow
                      label={u.canais.emergencia.label}
                      href={u.canais.emergencia.href}
                      display={u.canais.emergencia.display}
                    />
                    <CanalRow
                      label={u.canais.oficina.label}
                      href={u.canais.oficina.href}
                      display={u.canais.oficina.display}
                    />
                    <CanalRow
                      label={u.canais.atendimento.label}
                      href={u.canais.atendimento.href}
                      display={u.canais.atendimento.display}
                    />
                    <CanalRow
                      label={u.canais.financeiro.label}
                      href={u.canais.financeiro.href}
                      display={u.canais.financeiro.display}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid gap-6 sm:grid-cols-2 max-w-2xl mx-auto">
              <a
                href={COMERCIAL_WA}
                target="_blank"
                rel="noopener noreferrer"
                className="flex gap-4 rounded-2xl border border-border bg-card p-6 shadow-sm hover:border-amber-400/50 transition-colors"
              >
                <MessageCircle className="h-6 w-6 text-amber-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">WhatsApp comercial (matriz)</p>
                  <p className="text-muted-foreground mt-1">{COMERCIAL_WA_DISPLAY}</p>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex gap-4 rounded-2xl border border-border bg-card p-6 shadow-sm hover:border-amber-400/50 transition-colors"
              >
                <Mail className="h-6 w-6 text-amber-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">E-mail</p>
                  <p className="text-muted-foreground mt-1 break-all">{EMAIL}</p>
                </div>
              </a>
            </div>

            <p className="mt-10 text-center text-sm text-muted-foreground">
              Conteúdo inspirado no site institucional{' '}
              <a href="https://zehmotoca.com.br/" className="text-amber-600 hover:underline" target="_blank" rel="noreferrer">
                zehmotoca.com.br
              </a>
              . Condições comerciais e contratuais prevalecem sobre este resumo.
            </p>
          </div>
        </section>

        <footer className="border-t border-border bg-zinc-950 text-zinc-300 py-10 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2 text-white font-semibold font-[family-name:var(--font-heading)]">
              <Bike className="h-5 w-5 text-amber-400" />
              Zeh Motoca
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/cliente/login" className="hover:text-amber-400 transition-colors">
                Área do cliente
              </Link>
              <Link to="/login" className="hover:text-amber-400 transition-colors">
                Equipe (SIC)
              </Link>
              <Link to="/politica-de-privacidade" className="hover:text-amber-400 transition-colors">
                Política de privacidade
              </Link>
              <a href="https://zehmotoca.com.br/" target="_blank" rel="noreferrer" className="hover:text-amber-400 transition-colors">
                Site institucional
              </a>
            </div>
            <p className="text-xs text-zinc-500">© {new Date().getFullYear()} Zeh Motoca</p>
          </div>
        </footer>
      </div>
    </>
  );
}
